import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useError } from '#globalContexts/ErrorContext';
import { getErrorMessageForCode } from '#lib/errors/errorMessages';

import { creditsKeys } from '../../credits/models/credits/keys';
import {
	JOB_SUCCESS_MESSAGES,
	JOB_SUCCESS_TITLES,
	JOB_ERROR_MESSAGES,
	JOB_ERROR_TITLES,
} from '../constants/jobTypeNames';
import { companiesKeys } from '../models/companies/keys';
import { companyAnalysisKeys } from '../models/companyAnalysis/keys';
import { contactPeopleKeys } from '../models/contactPeople/keys';
import { prospectQueryKeys } from '../models/prospectQueries/keys';

const isFinished = (job) => job.status === 'completed' || job.status === 'failed';

/**
 * Hook to handle job status notifications and data refetching.
 * Notifies only for a job this hook has itself watched finish: the job list keeps finished
 * jobs for a day, so a job that is already finished when the list first arrives (page load,
 * switch to another query) was reported back then and must not be announced again.
 */
export const useJobNotifications = (runningJobs) => {
	const queryClient = useQueryClient();
	const { showError, showSuccess, showWarning } = useError();
	const unfinishedSeen = useRef(new Set());
	const notified = useRef(new Set());

	useEffect(() => {
		if (!runningJobs.length) return;

		runningJobs.forEach((job) => {
			const jobKey = `${job.jobType}_${job.bullJobId}`;

			if (!isFinished(job)) {
				unfinishedSeen.current.add(jobKey);
				return;
			}
			if (unfinishedSeen.current.has(jobKey) && !notified.current.has(jobKey)) {
				notified.current.add(jobKey);

				// Show notification based on job status
				if (job.status === 'failed') {
					showError({
						title: JOB_ERROR_TITLES[job.jobType] || 'Fehler aufgetreten',
						message:
							getErrorMessageForCode(job.errorCode) ||
							JOB_ERROR_MESSAGES[job.jobType] ||
							'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
						autoClose: false,
					});
				} else if (job.status === 'completed' && job.errorCode === 'INSUFFICIENT_COMPANY_CREDITS') {
					showWarning({
						title: 'Firmen-Guthaben aufgebraucht',
						message:
							'Ihr Guthaben hat nicht für alle Treffer gereicht. Wir haben so viele Unternehmen übernommen, wie Firmen-Credits verfügbar waren.',
						autoClose: false,
					});
				} else if (job.status === 'completed') {
					showSuccess({
						title: JOB_SUCCESS_TITLES[job.jobType] || 'Erfolgreich abgeschlossen!',
						message: JOB_SUCCESS_MESSAGES[job.jobType] || 'Der Vorgang wurde erfolgreich abgeschlossen.',
						autoClose: true,
						autoCloseDuration: 5000,
					});
				}

				// Invalidate queries when job completes successfully
				if (job.status === 'completed') {
					handleJobCompletion(job.jobType, queryClient);
				}
			}
		});
	}, [runningJobs, queryClient, showError, showSuccess, showWarning]);
};

/**
 * Invalidates the query caches affected by a completed job of the given type.
 */
const handleJobCompletion = (jobType, queryClient) => {
	switch (jobType) {
		case 'googleMapsScraper':
			setTimeout(() => {
				// Invalidate prospect queries
				queryClient.invalidateQueries({ queryKey: prospectQueryKeys.filtered(['b2bheroLeadSearch']) });
				queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
				// Invalidate companies
				queryClient.invalidateQueries({ queryKey: companiesKeys.all });
				// Invalidate credits as they were consumed
				queryClient.invalidateQueries({ queryKey: creditsKeys.wallet() });
			}, 1000);
			break;

		case 'localHrSearch':
			setTimeout(() => {
				// Invalidate prospect queries
				queryClient.invalidateQueries({
					queryKey: prospectQueryKeys.filtered(['b2bheroCommercialRegisterSearch']),
				});
				queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
				// Invalidate companies
				queryClient.invalidateQueries({ queryKey: companiesKeys.all });
				// Invalidate contacts since they are also fetched on HR search
				queryClient.invalidateQueries({ queryKey: contactPeopleKeys.all });
				// Invalidate credits as they were consumed
				queryClient.invalidateQueries({ queryKey: creditsKeys.wallet() });
			}, 1000);
			break;

		case 'contactEnrichment':
			setTimeout(() => {
				// Invalidate contacts and email lists
				queryClient.invalidateQueries({ queryKey: contactPeopleKeys.all });
				// The contactCount badge in the leads table comes flat from the companies read model
				queryClient.invalidateQueries({ queryKey: companiesKeys.all });
				// Invalidate credits as they were consumed
				queryClient.invalidateQueries({ queryKey: creditsKeys.wallet() });
			}, 1000);
			break;

		case 'companyAnalysis':
			setTimeout(() => {
				// Invalidate AI analyses
				queryClient.invalidateQueries({ queryKey: companyAnalysisKeys.all });
			}, 1000);
			break;

		default:
			break;
	}
};
